import { db } from '../firebase';
import {
  collection, getDocs, doc, addDoc, setDoc, updateDoc, deleteDoc,
  query, orderBy, serverTimestamp, getDoc, writeBatch
} from 'firebase/firestore';

// ─── Inquiries ─────────────────────────────────────────────────────
export const getInquiries = async () => {
  const q = query(collection(db, 'inquiries'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const markInquiryRead = (id) =>
  updateDoc(doc(db, 'inquiries', id), { read: true });

export const deleteInquiry = (id) =>
  deleteDoc(doc(db, 'inquiries', id));

// ─── Banners ───────────────────────────────────────────────────────
export const getBannersAdmin = async () => {
  const q = query(collection(db, 'home_banners'), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addBanner = (data) =>
  addDoc(collection(db, 'home_banners'), data);

export const updateBanner = (id, data) =>
  updateDoc(doc(db, 'home_banners', id), data);

export const deleteBanner = (id) =>
  deleteDoc(doc(db, 'home_banners', id));

// ─── Gallery (generic) ─────────────────────────────────────────────
export const getGalleryItems = async (colName) => {
  const q = query(collection(db, colName), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addGalleryItem = (colName, data) =>
  addDoc(collection(db, colName), data);

export const deleteGalleryItem = (colName, id) =>
  deleteDoc(doc(db, colName, id));

// ─── Our Work ──────────────────────────────────────────────────────
export const getOurWorkPhotosAdmin = () => getGalleryItems('ourwork_photos');
export const addOurWorkPhoto = (data) => addGalleryItem('ourwork_photos', data);
export const deleteOurWorkPhoto = (id) => deleteGalleryItem('ourwork_photos', id);

export const getOurWorkVideosAdmin = () => getGalleryItems('ourwork_videos');
export const addOurWorkVideo = (data) => addGalleryItem('ourwork_videos', data);
export const deleteOurWorkVideo = (id) => deleteGalleryItem('ourwork_videos', id);

export const getTutorialVideosAdmin = () => getGalleryItems('tutorial_videos');
export const addTutorialVideo = (data) => addGalleryItem('tutorial_videos', data);
export const deleteTutorialVideo = (id) => deleteGalleryItem('tutorial_videos', id);

// ─── Instagram Posts ───────────────────────────────────────────────
export const getInstagramAdmin = () => getGalleryItems('instagram_posts');
export const addInstagramPost = (data) => addGalleryItem('instagram_posts', data);
export const deleteInstagramPost = (id) => deleteGalleryItem('instagram_posts', id);

// ─── Testimonials ──────────────────────────────────────────────────
export const getTestimonialsAdmin = async (colName = 'testimonials') => {
  const q = query(collection(db, colName), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addTestimonial = (data, colName = 'testimonials') =>
  addDoc(collection(db, colName), data);

export const updateTestimonial = (id, data, colName = 'testimonials') =>
  updateDoc(doc(db, colName, id), data);

export const deleteTestimonial = (id, colName = 'testimonials') =>
  deleteDoc(doc(db, colName, id));

// ─── FAQs ──────────────────────────────────────────────────────────
export const getFAQsAdmin = async () => {
  const q = query(collection(db, 'faqs'), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addFAQ = (data) => addDoc(collection(db, 'faqs'), data);
export const updateFAQ = (id, data) => updateDoc(doc(db, 'faqs', id), data);
export const deleteFAQ = (id) => deleteDoc(doc(db, 'faqs', id));

// ─── Passcodes ─────────────────────────────────────────────────────
export const getPasscodesAdmin = async () => {
  const q = query(collection(db, 'bride_passcodes'), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addPasscode = (data) => addDoc(collection(db, 'bride_passcodes'), data);
export const deletePasscode = (id) => deleteDoc(doc(db, 'bride_passcodes', id));

// ─── Home Content ──────────────────────────────────────────────────
export const getHomeIntroAdmin = async () => {
  const snap = await getDoc(doc(db, 'home_intro', 'content'));
  return snap.exists() ? snap.data() : { heading: '', paragraph: '' };
};

export const updateHomeIntro = (data) =>
  setDoc(doc(db, 'home_intro', 'content'), data);

export const getWhyChooseAdmin = async () => {
  const snap = await getDoc(doc(db, 'home_why_choose', 'content'));
  return snap.exists() ? snap.data() : { image: '', points: [] };
};

export const updateWhyChoose = (data) =>
  setDoc(doc(db, 'home_why_choose', 'content'), data);

export const getInfoCardsAdmin = () => getGalleryItems('home_info_cards');
export const updateInfoCard = (id, data) => updateDoc(doc(db, 'home_info_cards', id), data);
export const addInfoCard = (data) => addDoc(collection(db, 'home_info_cards'), data);
export const deleteInfoCard = (id) => deleteDoc(doc(db, 'home_info_cards', id));

// ─── About Content ─────────────────────────────────────────────────
export const getAboutAdmin = async () => {
  const snap = await getDoc(doc(db, 'about_content', 'main'));
  return snap.exists() ? snap.data().Paragraph : '';
};

export const updateAbout = (paragraph) =>
  setDoc(doc(db, 'about_content', 'main'), { Paragraph: paragraph });

export const getMVAdmin = async () => {
  const q = query(collection(db, 'about_mission_vision'));
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return {
    mission: docs.find(d => d.Title === 'Mission')?.Description || '',
    vision: docs.find(d => d.Title === 'Vision')?.Description || '',
  };
};

export const updateMission = (text) =>
  setDoc(doc(db, 'about_mission_vision', 'Mission'), { Title: 'Mission', Description: text });

export const updateVision = (text) =>
  setDoc(doc(db, 'about_mission_vision', 'Vision'), { Title: 'Vision', Description: text });

export const getMilestonesAdmin = () => getGalleryItems('milestones');
export const addMilestone = (data) => addDoc(collection(db, 'milestones'), data);
export const updateMilestone = (id, data) => updateDoc(doc(db, 'milestones', id), data);
export const deleteMilestone = (id) => deleteDoc(doc(db, 'milestones', id));

// ─── Founder ───────────────────────────────────────────────────────
export const getFounderAdmin = async () => {
  const snap = await getDoc(doc(db, 'founder', 'content'));
  return snap.exists() ? snap.data() : {};
};

export const updateFounder = (data) =>
  setDoc(doc(db, 'founder', 'content'), data);

// ─── Vanity Images ─────────────────────────────────────────────────
export const getVanityAdmin = () => getGalleryItems('home_vanity_images');
export const addVanityImage = (data) => addDoc(collection(db, 'home_vanity_images'), data);
export const deleteVanityImage = (id) => deleteDoc(doc(db, 'home_vanity_images', id));

// ─── Blogs ─────────────────────────────────────────────────────────
export const getBlogsAdmin = async () => {
  const q = query(collection(db, 'blogs'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getBlogAdmin = async (id) => {
  const snap = await getDoc(doc(db, 'blogs', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const createBlog = (data) =>
  addDoc(collection(db, 'blogs'), { ...data, createdAt: serverTimestamp() });

export const updateBlog = (id, data) =>
  updateDoc(doc(db, 'blogs', id), data);

export const deleteBlog = (id) =>
  deleteDoc(doc(db, 'blogs', id));
